import React from "react";
import { Outlet, Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import {
  Box, Flex, HStack, Text, Avatar, Menu, MenuButton, MenuList,
  MenuItem, MenuDivider, Heading, useColorMode, useColorModeValue, IconButton,
} from "@chakra-ui/react";
import { SearchIcon, ChevronDownIcon, MoonIcon, SunIcon } from "@chakra-ui/icons";
import {
  FiFolder, FiActivity, FiSettings, FiLogOut, FiUser, FiGrid, FiMessageSquare, FiBarChart2, FiUsers, FiClock, FiCalendar, FiBell, FiZap, FiGift, FiDollarSign, FiHeart, FiFlag, FiBriefcase, FiAward, FiHome, FiFileText, FiTrendingUp, FiKey, FiShield, FiTool,
} from "react-icons/fi";
import { logout } from "../lib/api";
import NotificationBell from "./NotificationBell";
import BrandLogo from "./BrandLogo";
import SessionExpiryBanner from "./SessionExpiryBanner";

const SIDEBAR_W = "232px";
const TOPBAR_H = "60px";

// Build-mode flag — set to "hr" for the Quantiv Media HR app. Left configurable so the
// shell can be reused inside a different host app (the legacy non-HR sidebar
// remains as a fallback).
const MODE = (process.env.REACT_APP_MODE || "hr").toLowerCase();

function NavItem({ to, icon: Icon, label, active }) {
  // HR-mode: dark sidebar nav-items (matches WP portal screenshots).
  if (MODE === "hr") {
    return (
      <RouterLink to={to} style={{ display: "block", padding: "0 8px", textDecoration: "none" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "9px 12px",
            margin: "2px 0",
            borderRadius: 8,
            color: active ? "#FFFFFF" : "#A1A1A6",
            background: active ? "#E31E24" : "transparent",
            fontWeight: active ? 600 : 500,
            fontSize: 13,
            transition: "all 140ms",
            cursor: "pointer",
          }}
          onMouseEnter={(e) => { if (!active) { e.currentTarget.style.background = "rgba(255,255,255,0.06)"; e.currentTarget.style.color = "#FFFFFF"; } }}
          onMouseLeave={(e) => { if (!active) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#A1A1A6"; } }}
        >
          <Icon size={16} />
          <span style={{ flex: 1 }}>{label}</span>
        </div>
      </RouterLink>
    );
  }
  // Fallback (non-HR) mode: use the generic design-system nav-item.
  return (
    <RouterLink to={to} style={{ display: "block", padding: "0 8px" }}>
      <div className={`ds-nav-item ${active ? "active" : ""}`}>
        <Icon size={17} />
        <span style={{ flex: 1 }}>{label}</span>
      </div>
    </RouterLink>
  );
}

// Section label in the HR sidebar — matches the WP admin's small caps
// dividers ("PEOPLE & ORG", "ATTENDANCE", "LEAVE & WFH" ...).
function SectionLabel({ children, isHR }) {
  return (
    <Text
      fontSize="10px"
      color={isHR ? "#78787F" : "text.tertiary"}
      px={5}
      mt={5}
      mb={2}
      textTransform="uppercase"
      letterSpacing="0.10em"
      fontWeight={700}
    >
      {children}
    </Text>
  );
}

// HrSidebar — grouped WP-style sidebar (class-dfhr-admin.php parity).
// Admin sees the full admin menu; employee sees the equivalent portal items.
// Section order and labels match the WP plugin exactly.
function HrSidebar({ role, location, isHR, isActive }) {
  const isAdmin = role === "SUPER_ADMIN";
  const path = location.pathname;

  return (
    <>
      <NavItem to="/dashboard" icon={FiGrid} label="Dashboard" active={path.startsWith("/dashboard")} />

      <SectionLabel isHR={isHR}>People & Org</SectionLabel>
      {isAdmin ? (
        <>
          <NavItem to="/staff"        icon={FiUsers}      label="Staff"        active={path === "/staff" || path === "/hr"} />
          <NavItem to="/interns"      icon={FiAward}      label="Interns"      active={path.startsWith("/interns")} />
          <NavItem to="/departments"  icon={FiBriefcase}  label="Departments"  active={path.startsWith("/departments")} />
          <NavItem to="/password-resets" icon={FiKey}     label="Password Resets" active={path.startsWith("/password-resets")} />
          {/* Designations nav hidden — org doesn't use designations. Restore this line to bring it back. */}
          {/* <NavItem to="/designations" icon={FiAward}      label="Designations" active={path.startsWith("/designations")} /> */}
        </>
      ) : (
        <NavItem to="/profile" icon={FiUser} label="My Profile" active={isActive("/profile")} />
      )}

      <SectionLabel isHR={isHR}>Attendance</SectionLabel>
      <NavItem to="/attendance" icon={FiClock} label="Attendance" active={path.startsWith("/attendance")} />
      <NavItem to="/regularisation" icon={FiTool} label={isAdmin ? "Regularisation" : "Fix Attendance"} active={path.startsWith("/regularisation")} />
      <NavItem to="/holidays"   icon={FiGift}  label="Holidays"   active={path.startsWith("/holidays")} />

      <SectionLabel isHR={isHR}>Leave & WFH</SectionLabel>
      <NavItem to="/leaves" icon={FiCalendar} label={isAdmin ? "Leave Requests" : "My Leaves"} active={path === "/leaves"} />
      <NavItem to="/wfh"    icon={FiHome}     label={isAdmin ? "WFH Requests"   : "WFH"}        active={path.startsWith("/wfh")} />
      {isAdmin && (
        <NavItem to="/leave-balances" icon={FiTrendingUp} label="Leave Balances" active={path.startsWith("/leave-balances")} />
      )}

      <SectionLabel isHR={isHR}>Work</SectionLabel>
      <NavItem to="/notices" icon={FiBell} label="Notices" active={path.startsWith("/notices")} />

      <SectionLabel isHR={isHR}>Payroll & Reports</SectionLabel>
      <NavItem to="/payroll" icon={FiDollarSign} label={isAdmin ? "Payroll" : "My Payslips"} active={path === "/payroll"} />
      {isAdmin && (
        <NavItem to="/payroll-run" icon={FiTrendingUp} label="Payroll Run" active={path.startsWith("/payroll-run")} />
      )}
      {isAdmin && (
        <NavItem to="/reports" icon={FiFileText} label="Reports" active={path.startsWith("/reports")} />
      )}

      <SectionLabel isHR={isHR}>Content Tools</SectionLabel>
      <NavItem to="/prompts" icon={FiZap} label="Prompts" active={path.startsWith("/prompts")} />
    </>
  );
}

function Sidebar() {
  const location = useLocation();
  const role = localStorage.getItem("role");
  const username = localStorage.getItem("username") || "User";
  const email = localStorage.getItem("email") || "";

  const isActive = (p) => {
    if (p === "/projects") return location.pathname.startsWith("/projects");
    if (p === "/admin") return location.pathname.startsWith("/admin");
    if (p === "/analytics") return location.pathname.startsWith("/analytics");
    if (p === "/chat") return location.pathname.startsWith("/chat");
    if (p === "/tasks") return location.pathname.startsWith("/tasks");
    return location.pathname === p;
  };

  // WP portal screenshots show a dark navy sidebar in BOTH admin and employee
  // views. In HR mode we paint the whole shell dark; fallback mode stays light.
  const isHR = MODE === "hr";

  return (
    <Flex
      direction="column"
      w={SIDEBAR_W}
      h="100vh"
      bg={isHR ? "#000000" : "bg.surface"}
      borderRight="1px solid"
      borderColor={isHR ? "rgba(255,255,255,0.06)" : "border.subtle"}
      position="fixed"
      left={0}
      top={0}
      zIndex={20}
    >
      <Flex align="center" h={TOPBAR_H} px={5} borderBottom="1px solid" borderColor={isHR ? "rgba(255,255,255,0.06)" : "border.subtle"}>
        {isHR ? (
          <>
            {/* Real Quantiv mark, replacing the hand-rolled blue "DF" tile.
                Same 36px box and 12px gutter, so the header geometry is unchanged. */}
            <Box mr={3} display="flex" alignItems="center">
              <BrandLogo variant="mark" size={36} />
            </Box>
            <Heading size="sm" fontWeight={700} letterSpacing="-0.01em" color="white">
              Quantiv Media
            </Heading>
          </>
        ) : (
          <>
            <Box
              w={7} h={7}
              bgGradient="linear(135deg, accent.primary, #7C5CFF)"
              rounded="lg"
              mr={3}
              boxShadow="elev.sm"
            />
            <Heading size="sm" fontWeight={600} letterSpacing="-0.01em" color="text.primary">
              Workspace
            </Heading>
          </>
        )}
      </Flex>

      <Box flex="1" py={3} overflowY="auto">
        {!isHR && (
          <Text fontSize="10px" color="text.tertiary" px={5} mb={2} textTransform="uppercase" letterSpacing="0.08em" fontWeight={600}>
            Workspace
          </Text>
        )}
        {/* HR mode — sidebar mirrors the WP admin menu structure from
            class-dfhr-admin.php (grouped sections: People & Org, Attendance,
            Leave & WFH, Work, Payroll & Reports, Content Tools). Admin sees
            all admin items; employee sees their own equivalents. */}
        {MODE === "hr" ? (
          <HrSidebar role={role} location={location} isHR={isHR} isActive={isActive} />
        ) : (
          <>
            <NavItem to="/dashboard" icon={FiGrid} label="Dashboard" active={isActive("/dashboard")} />
            <NavItem to="/projects" icon={FiFolder} label="Projects" active={isActive("/projects")} />
            <NavItem to="/tasks" icon={FiActivity} label="Tasks" active={isActive("/tasks")} />
            <NavItem to="/chat" icon={FiMessageSquare} label="Chat" active={isActive("/chat")} />
            <NavItem to="/profile" icon={FiUser} label="My Profile" active={isActive("/profile")} />
          </>
        )}

        {role === "SUPER_ADMIN" && MODE !== "hr" && (
          <>
            <Text fontSize="10px" color={isHR ? "#78787F" : "text.tertiary"} px={5} mt={6} mb={2} textTransform="uppercase" letterSpacing="0.08em" fontWeight={600}>
              Admin
            </Text>
            <NavItem to="/admin" icon={FiSettings} label="Admin Console" active={isActive("/admin")} />
            <NavItem to="/analytics" icon={FiBarChart2} label="Analytics" active={isActive("/analytics")} />
          </>
        )}

        {/* HR admin extras (Settings) — also lives on a horizontal divider so
            it's grouped separately from the WP-style section list above. */}
        {role === "SUPER_ADMIN" && MODE === "hr" && (
          <Box mt={4} pt={3} borderTop="1px solid" borderColor="rgba(255,255,255,0.06)">
            <NavItem to="/admin" icon={FiShield} label="Admin Console" active={isActive("/admin")} />
            <NavItem to="/settings" icon={FiSettings} label="Settings" active={isActive("/settings")} />
          </Box>
        )}
      </Box>

      <Box borderTop="1px solid" borderColor={isHR ? "rgba(255,255,255,0.06)" : "border.subtle"} p={3}>
        <HStack spacing={3} px={2} py={2} rounded="lg" _hover={{ bg: isHR ? "rgba(255,255,255,0.06)" : "bg.subtle" }} transition="background 160ms">
          <Avatar size="sm" name={username} bg="accent.primary" color="white" />
          <Box flex="1" minW={0}>
            <Text fontSize="sm" fontWeight={500} color={isHR ? "white" : "text.primary"} noOfLines={1}>{username}</Text>
            <Text fontSize="xs" color={isHR ? "#A1A1A6" : "text.tertiary"} noOfLines={1}>{email}</Text>
          </Box>
        </HStack>
        {/* Logout link — WP portal parity (Screen E5 shows ← Logout at the bottom). */}
        {isHR && (
          <Box
            as="button"
            mt={2}
            w="100%"
            px={3}
            py={2}
            rounded="lg"
            display="flex"
            alignItems="center"
            gap={2}
            color="#A1A1A6"
            _hover={{ bg: "rgba(220, 38, 38, 0.12)", color: "#FCA5A5" }}
            transition="all 160ms"
            onClick={() => { logout(); window.location.href = "/login"; }}
          >
            <FiLogOut size={16} />
            <Text fontSize="sm" fontWeight={500}>← Logout</Text>
          </Box>
        )}
      </Box>
    </Flex>
  );
}

function ThemeToggle() {
  const { colorMode, toggleColorMode } = useColorMode();
  return (
    <IconButton
      aria-label="Toggle color mode"
      variant="ghost"
      size="sm"
      icon={colorMode === "light" ? <MoonIcon /> : <SunIcon />}
      onClick={toggleColorMode}
      color="text.secondary"
      _hover={{ bg: "bg.subtle", color: "text.primary" }}
      rounded="md"
    />
  );
}

function TopBar({ title, breadcrumb, actions }) {
  const navigate = useNavigate();
  const username = localStorage.getItem("username") || "User";
  const role = localStorage.getItem("role");

  function signOut() {
    logout();
    navigate("/login");
  }

  return (
    <Flex
      h={TOPBAR_H}
      className="ds-glass"
      align="center"
      px={6}
      gap={4}
      position="sticky"
      top={0}
      zIndex={10}
    >
      <Box>
        {breadcrumb && (
          <Text fontSize="11px" color="text.tertiary" fontWeight={500}>{breadcrumb}</Text>
        )}
        {title && (
          <Heading size="md" fontWeight={600} letterSpacing="-0.02em" color="text.primary">{title}</Heading>
        )}
      </Box>
      <Box flex="1" />

      {/* Search affordance. The placeholder was text.tertiary, which is 4.20:1
          on bg.subtle in dark mode — an AA miss; text.secondary is 7.15:1. */}
      <HStack
        display={{ base: "none", md: "flex" }}
        spacing={2}
        bg="bg.subtle"
        rounded="md"
        px={3}
        py={1.5}
        minW="240px"
        color="text.secondary"
        fontSize="13px"
        cursor="text"
        _hover={{ bg: "bg.canvas" }}
        transition="background 160ms"
      >
        <SearchIcon boxSize={3.5} />
        <Text flex="1">Search…</Text>
        <Text fontSize="10px" px={1.5} py={0.5} rounded="sm" bg="bg.surface" border="1px solid" borderColor="border.subtle" fontFamily="mono">
          ⌘K
        </Text>
      </HStack>

      {actions}
      <ThemeToggle />
      <NotificationBell />
      <Menu>
        <MenuButton>
          <HStack px={2} py={1} rounded="md" _hover={{ bg: "bg.subtle" }} cursor="pointer" transition="background 160ms">
            <Avatar size="sm" name={username} bg="accent.primary" color="white" />
            <Box display={{ base: "none", md: "block" }} textAlign="left">
              <Text fontSize="sm" fontWeight={500} lineHeight={1.2} color="text.primary">{username}</Text>
              <Text fontSize="10px" color="text.tertiary" textTransform="uppercase" letterSpacing="0.05em">{role}</Text>
            </Box>
            <ChevronDownIcon color="text.tertiary" />
          </HStack>
        </MenuButton>
        <MenuList>
          <MenuItem icon={<FiUser />} onClick={() => navigate("/profile")}>My Profile</MenuItem>
          {role === "SUPER_ADMIN" && <MenuItem icon={<FiSettings />} onClick={() => navigate("/admin")}>Admin Console</MenuItem>}
          <MenuDivider />
          <MenuItem icon={<FiLogOut />} onClick={signOut} color="danger">Sign out</MenuItem>
        </MenuList>
      </Menu>
    </Flex>
  );
}

const ShellContext = React.createContext({ setShellHeader: () => {} });

export function useShellHeader(value, deps = []) {
  const ctx = React.useContext(ShellContext);
  React.useEffect(() => {
    ctx.setShellHeader(value);
    return () => ctx.setShellHeader({});
    // eslint-disable-next-line
  }, deps);
}

export default function AppShell() {
  const [header, setHeader] = React.useState({});
  return (
    <ShellContext.Provider value={{ setShellHeader: setHeader }}>
      <Flex bg="bg.canvas" minH="100vh">
        <Sidebar />
        <Box flex="1" ml={SIDEBAR_W}>
          <TopBar title={header.title} breadcrumb={header.breadcrumb} actions={header.actions} />
          <SessionExpiryBanner />
          <Box p={{ base: 4, md: 6 }}>
            <Outlet />
          </Box>
        </Box>
      </Flex>
    </ShellContext.Provider>
  );
}
